import type { LicenseDateRange, OrganizationLicenses } from '../../../../utils/types/admin_types';
import type { LicenseCatalogKey } from '../interfaces';

export interface ILicenseDatePickerValue {
  startDate: string | null;
  endDate: string | null;
}

/** Extracts a `YYYY-MM-DD` string from a DatePicker change payload. */
export const extractLicenseDateString = (
  value: ILicenseDatePickerValue | string | null,
  field: keyof LicenseDateRange,
): string => {
  if (typeof value === 'string') return value;
  if (value && typeof value === 'object') {
    if (field === 'monthlyCredits') return '';
    return field === 'startDate' ? value.startDate ?? '' : value.endDate ?? '';
  }
  return '';
};

/** Formats a metered license allowance for a number input. Blank = unlimited. */
export const formatMonthlyCredits = (licenses: OrganizationLicenses, key: LicenseCatalogKey): string => {
  const value = licenses[key]?.monthlyCredits;
  return value === null || value === undefined ? '' : String(value);
};

/** Parses a credits input. Blank = unlimited (null), 0 = blocked, NaN = null. */
export const parseMonthlyCredits = (raw: string): number | null => {
  const trimmed = raw.trim();
  if (trimmed === '') return null;
  const parsed = Math.max(0, Math.floor(Number(trimmed)));
  return Number.isNaN(parsed) ? null : parsed;
};

/** True when every active license has both start and end dates. */
export const areLicenseDatesComplete = (
  licenses: OrganizationLicenses,
  keys: LicenseCatalogKey[],
): boolean =>
  !keys.some((key) => {
    const license = licenses[key];
    return license !== undefined && (!license.startDate || !license.endDate);
  });

/** Serializes comma-separated domains input into a clean string array. */
export const parseDomainsInput = (raw: string): string[] =>
  raw.split(',').map((domain) => domain.trim()).filter(Boolean);
